'use client';

import { useEffect, useState } from 'react';
import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { PostCard } from '../../blog/PostCard';
import { Button } from '../../ui/button';
import { PageType } from '../../Router';
import { cn } from '../../ui/utils';
import {
  getPublishedPosts, getPublishedPostsByCategory,
  type BlogPostDocument, type BlogCategoryDocument,
} from '../../../firebase/blog-db';
import { FileQuestion } from 'lucide-react';

interface AllNewsPageProps {
  onNavigate: (page: PageType) => void;
  categories: BlogCategoryDocument[];
  posts: BlogPostDocument[];
  hasMore: boolean;
}

const PAGE_SIZE = 8;

export function AllNewsPage({ onNavigate, categories, posts: initialPosts, hasMore: initialHasMore }: AllNewsPageProps) {
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null); // null = "All Posts"
  const [posts, setPosts] = useState<BlogPostDocument[]>(initialPosts);
  const [cursor, setCursor] = useState<string | undefined>(initialPosts.at(-1)?.publishedAt ?? undefined);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  // Server already rendered the initial "All Posts" page — this effect only
  // ever fires for genuinely interactive tab switches after that, never on
  // first mount (the "All Posts" tab's data matches what was passed in).
  useEffect(() => {
    if (activeCategoryId === null) {
      setPosts(initialPosts);
      setCursor(initialPosts.at(-1)?.publishedAt ?? undefined);
      setHasMore(initialHasMore);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const page = await getPublishedPostsByCategory(activeCategoryId, PAGE_SIZE);
        if (cancelled) return;
        setPosts(page.posts);
        setCursor(page.nextCursor);
        setHasMore(page.posts.length === PAGE_SIZE);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCategoryId]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = activeCategoryId
        ? await getPublishedPostsByCategory(activeCategoryId, PAGE_SIZE, cursor)
        : await getPublishedPosts(PAGE_SIZE, cursor);
      setPosts(prev => [...prev, ...page.posts]);
      setCursor(page.nextCursor);
      setHasMore(page.posts.length === PAGE_SIZE);
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const tabs = [{ id: null as string | null, name: 'All Posts' }, ...categories.map(c => ({ id: c.id!, name: c.name }))];

  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <div className="pt-14">
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <h1 className="font-blocky text-3xl text-gray-900 mb-8">All News</h1>
            <nav className="flex flex-wrap gap-x-8 gap-y-3" aria-label="Filter by category">
              {tabs.map(tab => (
                <button
                  key={tab.id ?? 'all'}
                  onClick={() => setActiveCategoryId(tab.id)}
                  className={cn(
                    'font-blocky text-sm sm:text-base transition-colors',
                    activeCategoryId === tab.id ? 'text-hustlance-primary' : 'text-gray-900 hover:text-hustlance-primary'
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="space-y-6 animate-pulse">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-72 bg-gray-100" />)}
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-gray-500 mb-4">We couldn't load these articles right now.</p>
              <Button onClick={() => window.location.reload()}>Try again</Button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No articles here yet.</p>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {posts.map(post => <PostCard key={post.id} post={post} variant="listRow" />)}
              </div>
              {hasMore && (
                <div className="text-center mt-10">
                  <Button variant="outline" size="lg" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? 'Loading...' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
